import { useEffect, useState } from 'react'

const API = '/api/clinico'
type Tipo = 'adulto' | 'pediatrica'
type Respuestas = Record<string, unknown>
interface Alerta { nivel: 'roja' | 'amarilla'; codigo: string; texto: string }
interface AnamnesisActual {
  id: string
  tipo: Tipo
  respuestas: Respuestas
  registradaAt: string
  alertas: Alerta[]
}
interface ConsultaHistorial {
  id: string; inicio: string | null; estado: string; notas: string | null
  anamnesisOdontologica: Record<string, unknown> | null
}

const preguntasAdulto = [
  ['enfermedades.diabetes', 'Diabetes'],
  ['enfermedades.hipertension', 'Hipertensión'],
  ['enfermedades.cardiaca', 'Problemas cardíacos'],
  ['enfermedades.asma', 'Asma'],
  ['medicamentos.anticoagulantes', 'Anticoagulantes'],
  ['medicamentos.antibioticos', 'Antibióticos'],
  ['alergias.penicilina', 'Alergia a penicilina'],
  ['alergias.anestesia', 'Alergia a anestesia'],
  ['alergias.latex', 'Alergia a látex'],
  ['sangradoExcesivo', 'Sangrado excesivo o mala cicatrización'],
  ['fuma', 'Fuma'],
  ['alcohol', 'Consume alcohol'],
] as const

const preguntasKids: readonly (readonly [string, string])[] = []

function leer(datos: Respuestas, ruta: string): unknown {
  return ruta.split('.').reduce<unknown>((v, k) =>
    v && typeof v === 'object' ? (v as Respuestas)[k] : undefined, datos)
}

function escribir(datos: Respuestas, ruta: string, valor: unknown): Respuestas {
  const copia = structuredClone(datos)
  const partes = ruta.split('.')
  let actual = copia
  partes.slice(0, -1).forEach((p) => {
    if (!actual[p] || typeof actual[p] !== 'object') actual[p] = {}
    actual = actual[p] as Respuestas
  })
  actual[partes.at(-1)!] = valor
  return copia
}

export function Anamnesis({ clienteId, esMenor, compacta = false }: { clienteId: string; esMenor: boolean; compacta?: boolean }) {
  const [actual, setActual] = useState<AnamnesisActual | null>(null)
  const [historial, setHistorial] = useState<{ id: string; registradaAt: string }[]>([])
  const [tipo, setTipo] = useState<Tipo>(esMenor ? 'pediatrica' : 'adulto')
  const [datos, setDatos] = useState<Respuestas>({})
  const [editando, setEditando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [mensaje, setMensaje] = useState<string | null>(null)
  const [consultas, setConsultas] = useState<ConsultaHistorial[]>([])
  const [expandida, setExpandida] = useState(!compacta)

  async function cargar() {
    const r = await fetch(`${API}/${clienteId}/anamnesis`, { credentials: 'include' })
    if (!r.ok) throw new Error('No se pudo cargar la anamnesis')
    const cuerpo = await r.json() as { actual: AnamnesisActual | null; historial: typeof historial }
    setActual(cuerpo.actual)
    setHistorial(cuerpo.historial)
    if (cuerpo.actual) {
      setTipo(cuerpo.actual.tipo)
      setDatos(cuerpo.actual.respuestas)
    }
  }

  useEffect(() => { void cargar().catch(() => setMensaje('No se pudo cargar la anamnesis')) }, [clienteId])
  useEffect(() => {
    if (compacta) return
    void fetch(`/api/clientes/${clienteId}/ficha`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error()))
      .then((ficha: { historial: ConsultaHistorial[] }) => setConsultas(ficha.historial))
      .catch(() => setConsultas([]))
  }, [clienteId, compacta])

  async function guardar() {
    setGuardando(true)
    setMensaje(null)
    try {
      const r = await fetch(`${API}/${clienteId}/anamnesis`, {
        method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tipo, respuestas: datos }),
      })
      if (!r.ok) throw new Error('No se pudo guardar la anamnesis')
      await cargar()
      setEditando(false)
      setMensaje('Nueva versión guardada')
      if (compacta) setExpandida(false)
    } catch (e) {
      setMensaje(e instanceof Error ? e.message : 'No se pudo guardar')
    } finally { setGuardando(false) }
  }

  const preguntas = tipo === 'pediatrica' ? preguntasKids : preguntasAdulto
  const alergiaDeclarada = actual
    ? leer(actual.respuestas, 'alergias.penicilina') === true
      || String(leer(actual.respuestas, 'alergias.detalles') ?? '').trim().length > 0
    : false
  if (compacta && !expandida) return <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><p className="text-xs font-semibold uppercase tracking-wide text-teal-700">Seguridad clínica</p><h2 className="mt-1 font-semibold text-slate-900">Antecedentes médicos</h2><p className="mt-1 text-xs text-slate-500">{actual ? `Última revisión: ${new Date(actual.registradaAt).toLocaleDateString('es-PY')}` : 'Pendiente de completar'}</p></div>
      <button type="button" onClick={() => setExpandida(true)} className="rounded-lg border border-teal-300 px-3 py-2 text-sm font-medium text-teal-800">Revisar antecedentes</button>
    </div>
    {actual && actual.alertas.length === 0 && !alergiaDeclarada && <p className="mt-3 inline-flex rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800">✓ Sin alertas médicas registradas</p>}
    {alergiaDeclarada && actual?.alertas.length === 0 && <p className="mt-3 rounded-lg bg-rose-100 p-3 text-sm font-bold text-rose-900">ALERGIA: {String(leer(actual?.respuestas ?? {}, 'alergias.detalles') || 'Penicilina')}</p>}
    {actual?.alertas.map((a) => <p key={a.codigo} className={`mt-2 rounded-lg p-3 text-sm font-bold ${a.nivel === 'roja' ? 'bg-rose-100 text-rose-900' : 'bg-amber-100 text-amber-900'}`}>{a.texto}</p>)}
  </section>
  return (
    <section className="mt-5 print:mt-0" id="anamnesis-imprimible">
      <div className="mb-3 flex flex-wrap items-center gap-2 print:hidden">
        <div><h1 className="text-lg font-semibold text-slate-900">Anamnesis médica</h1><p className="text-sm text-slate-500">Historia médica general del paciente</p></div>
        {compacta && <button type="button" onClick={() => setExpandida(false)} className="ml-auto rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">Volver al resumen</button>}
        <button type="button" onClick={() => { setEditando(true); setDatos(actual?.respuestas ?? {}) }}
          className={compacta ? 'text-sm text-teal-700' : 'ml-auto text-sm text-teal-700'}>
          {actual ? 'Actualizar (nueva versión)' : 'Completar'}
        </button>
        {actual && <button type="button" onClick={() => window.print()} className="text-sm text-slate-600">Imprimir anamnesis</button>}
      </div>

      {actual?.alertas.map((a) => <p key={a.codigo} className={`mb-2 rounded-lg p-3 text-sm font-bold ${a.nivel === 'roja' ? 'bg-rose-100 text-rose-900' : 'bg-amber-100 text-amber-900'}`}>{a.texto}</p>)}
      {mensaje && <p className="mb-2 text-sm text-slate-600 print:hidden">{mensaje}</p>}

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        {!actual && !editando && <p className="text-sm text-slate-400">Pendiente antes de la primera atención.</p>}
        {(actual || editando) && <>
          <h2 className="mb-3 text-base font-semibold">{tipo === 'pediatrica' ? 'Anamnesis pediátrica' : 'Antecedentes y riesgos'}</h2>
          {(leer(datos, 'alergias.penicilina') === true || String(leer(datos, 'alergias.detalles') ?? '').trim()) && (
            <div className="mb-3 rounded-lg border-2 border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
              <strong>ALERGIAS:</strong> {leer(datos, 'alergias.penicilina') === true ? 'Penicilina. ' : ''}{String(leer(datos, 'alergias.detalles') ?? '')}
            </div>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            {preguntas.map(([ruta, etiqueta]) => <label key={ruta} className="flex min-h-10 items-center gap-2 rounded-lg border border-slate-100 px-3 text-sm">
              <input type="checkbox" disabled={!editando} checked={leer(datos, ruta) === true}
                onChange={(e) => setDatos(escribir(datos, ruta, e.target.checked))} /> {etiqueta}
            </label>)}
          </div>
          {tipo === 'adulto' && <Embarazo datos={datos} editando={editando} onCambiar={(ruta, valor) => setDatos(escribir(datos, ruta, valor))} />}
          {tipo === 'pediatrica' && <CamposPediatricos datos={datos} editando={editando} onCambiar={(ruta, valor) => setDatos(escribir(datos, ruta, valor))} />}
          <Campos tipo={tipo} datos={datos} editando={editando} onCambiar={(ruta, valor) => setDatos(escribir(datos, ruta, valor))} />
          {editando && <div className="mt-4 flex gap-2 print:hidden">
            <button type="button" disabled={guardando} onClick={() => void guardar()} className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white">{guardando ? 'Guardando…' : 'Guardar versión'}</button>
            <button type="button" onClick={() => setEditando(false)} className="px-3 text-sm text-slate-500">Cancelar</button>
          </div>}
          {actual && <p className="mt-4 text-xs text-slate-500">Anamnesis del {new Date(actual.registradaAt).toLocaleDateString('es-PY')}</p>}
          <div className="mt-12 hidden border-t border-slate-500 pt-2 text-center text-sm print:block">Firma del paciente o responsable</div>
        </>}
      </div>
      {historial.length > 1 && <details className="mt-2 text-sm print:hidden"><summary className="cursor-pointer text-slate-600">Historial ({historial.length} versiones)</summary><ul className="mt-1 pl-5">{historial.map((h) => <li key={h.id}>Anamnesis del {new Date(h.registradaAt).toLocaleDateString('es-PY')}</li>)}</ul></details>}
      {!compacta && <HistorialConsultas consultas={consultas} />}
    </section>
  )
}

function Campos({ tipo, datos, editando, onCambiar }: { tipo: Tipo; datos: Respuestas; editando: boolean; onCambiar: (ruta: string, valor: unknown) => void }) {
  const campos: readonly (readonly [string, string])[] = tipo === 'adulto' ? [
    ['enfermedades.detalles', 'Otras enfermedades o detalles'], ['medicamentos.detalles', 'Otros medicamentos'],
    ['alergias.detalles', 'Otras alergias o reacciones'],
  ] : [
    ['alergias.detalles', 'Alergias y medicamentos pediátricos'],
  ]
  return <div className="mt-5 grid gap-4 sm:grid-cols-2">{campos.map(([ruta, etiqueta]) => <label key={ruta} className="rounded-lg bg-slate-50 p-3 text-xs font-medium text-slate-600">{etiqueta}<textarea disabled={!editando} rows={3} value={String(leer(datos, ruta) ?? '')} onChange={(e) => onCambiar(ruta, e.target.value)} className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-800 disabled:bg-white" /></label>)}</div>
}

function CamposPediatricos({ datos, editando, onCambiar }: { datos: Respuestas; editando: boolean; onCambiar: (ruta: string, valor: unknown) => void }) {
  return <div className="mt-5 space-y-4">
    <Bloque titulo="Nacimiento">
      <SiNo etiqueta="¿Fue prematuro?" ruta="parto.prematuro" datos={datos} editando={editando} onCambiar={onCambiar} />
      <label className="text-xs text-slate-500">Peso al nacer (kg)<input type="number" min="0.3" max="10" step="0.01" disabled={!editando} value={String(leer(datos, 'parto.pesoNacerKg') ?? '')} onChange={(e) => onCambiar('parto.pesoNacerKg', e.target.value)} className="mt-1 min-h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" /></label>
      <SiNo etiqueta="¿Hubo complicaciones durante el embarazo o parto?" ruta="parto.tuvoComplicaciones" datos={datos} editando={editando} onCambiar={onCambiar} />
      {leer(datos, 'parto.tuvoComplicaciones') === true && <label className="text-xs text-slate-500 sm:col-span-2">¿Cuáles?<textarea rows={2} disabled={!editando} value={String(leer(datos, 'parto.complicaciones') ?? '')} onChange={(e) => onCambiar('parto.complicaciones', e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm" /></label>}
    </Bloque>
    <Bloque titulo="Lactancia y mamadera">
      <div className="rounded-lg bg-slate-50 p-3 sm:col-span-2">
        <SiNo etiqueta="¿Recibió lactancia materna?" ruta="lactancia.pecho" datos={datos} editando={editando} onCambiar={onCambiar} />
        {leer(datos, 'lactancia.pecho') === true && <div className="mt-3 max-w-sm"><Duracion prefijo="lactancia.pechoHasta" etiqueta="¿Hasta qué edad recibió lactancia materna?" datos={datos} editando={editando} onCambiar={onCambiar} /></div>}
      </div>
      <div className="rounded-lg bg-slate-50 p-3 sm:col-span-2">
        <SiNo etiqueta="¿Usa o usó mamadera?" ruta="lactancia.mamadera" datos={datos} editando={editando} onCambiar={onCambiar} />
        {leer(datos, 'lactancia.mamadera') === true && <div className="mt-3 grid gap-4 border-t border-slate-200 pt-3 sm:grid-cols-2"><Duracion prefijo="lactancia.mamaderaHasta" etiqueta="¿Hasta qué edad usó mamadera?" datos={datos} editando={editando} onCambiar={onCambiar} /><SiNo etiqueta="¿Se duerme o dormía con la mamadera?" ruta="lactancia.duermeMamadera" datos={datos} editando={editando} onCambiar={onCambiar} /></div>}
      </div>
    </Bloque>
    <Bloque titulo="Conducta en consulta">
      <label className="text-xs text-slate-500 sm:col-span-2">Miedo al dentista<select disabled={!editando} value={String(leer(datos, 'miedoDentista') ?? '')} onChange={(e) => onCambiar('miedoDentista', e.target.value)} className="mt-1 min-h-11 w-full rounded-lg border border-slate-300 px-3 text-sm"><option value="">Seleccionar…</option><option value="1">😁 1 · Sin miedo</option><option value="2">🙂 2 · Tranquilo</option><option value="3">😐 3 · Algo nervioso</option><option value="4">😟 4 · Con miedo</option><option value="5">😰 5 · Mucho miedo</option></select></label>
    </Bloque>
    <Bloque titulo="Hábitos orales">
      <SiNo etiqueta="¿Usa chupete?" ruta="habitos.chupete" datos={datos} editando={editando} onCambiar={onCambiar} />
      <SiNo etiqueta="¿Se chupa el dedo?" ruta="habitos.dedo" datos={datos} editando={editando} onCambiar={onCambiar} />
      <SiNo etiqueta="¿Respira por la boca?" ruta="habitos.respiraBoca" datos={datos} editando={editando} onCambiar={onCambiar} />
      <SiNo etiqueta="¿Tiene bruxismo?" ruta="habitos.bruxismo" datos={datos} editando={editando} onCambiar={onCambiar} />
    </Bloque>
    <Bloque titulo="Salud general y alimentación">
      <SiNo etiqueta="¿Consume frecuentemente azúcar o golosinas?" ruta="alimentacion.azucar" datos={datos} editando={editando} onCambiar={onCambiar} />
      <SiNo etiqueta="¿Tiene las vacunas al día?" ruta="vacunasAlDia" datos={datos} editando={editando} onCambiar={onCambiar} />
      <SiNo etiqueta="¿Tiene alergia a la penicilina?" ruta="alergias.penicilina" datos={datos} editando={editando} onCambiar={onCambiar} />
      <SiNo etiqueta="¿Tiene alergia a medicamentos pediátricos?" ruta="alergias.antibioticosPediatricos" datos={datos} editando={editando} onCambiar={onCambiar} />
    </Bloque>
  </div>
}

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return <fieldset className="rounded-xl border border-slate-200 p-4"><legend className="px-1 text-sm font-semibold text-slate-700">{titulo}</legend><div className="grid gap-4 sm:grid-cols-2">{children}</div></fieldset>
}

function SiNo({ etiqueta, ruta, datos, editando, onCambiar }: { etiqueta: string; ruta: string; datos: Respuestas; editando: boolean; onCambiar: (ruta: string, valor: unknown) => void }) {
  return <div><p className="text-sm text-slate-700">{etiqueta}</p><div className="mt-2 flex gap-4 text-sm">{([['Sí', true], ['No', false]] as const).map(([texto, valor]) => <label key={texto} className="flex items-center gap-2"><input type="radio" disabled={!editando} checked={leer(datos, ruta) === valor} onChange={() => onCambiar(ruta, valor)} />{texto}</label>)}</div></div>
}

function Duracion({ prefijo, etiqueta, datos, editando, onCambiar }: { prefijo: string; etiqueta: string; datos: Respuestas; editando: boolean; onCambiar: (ruta: string, valor: unknown) => void }) {
  return <div><label className="text-xs text-slate-500">{etiqueta}</label><div className="mt-1 flex gap-2"><input type="number" min="0" disabled={!editando} value={String(leer(datos, `${prefijo}.cantidad`) ?? '')} onChange={(e) => onCambiar(`${prefijo}.cantidad`, e.target.value)} className="min-h-10 min-w-0 flex-1 rounded-lg border border-slate-300 px-3 text-sm" /><select disabled={!editando} value={String(leer(datos, `${prefijo}.unidad`) ?? 'meses')} onChange={(e) => onCambiar(`${prefijo}.unidad`, e.target.value)} className="rounded-lg border border-slate-300 px-2 text-sm"><option value="meses">meses</option><option value="años">años</option></select></div></div>
}

function Embarazo({ datos, editando, onCambiar }: { datos: Respuestas; editando: boolean; onCambiar: (ruta: string, valor: unknown) => void }) {
  const embarazada = leer(datos, 'embarazo.si') === true
  return <div className="mt-5 rounded-lg border border-slate-200 p-3">
    <p className="text-sm font-medium text-slate-700">¿Está embarazada?</p>
    <div className="mt-2 flex gap-4 text-sm">
      {([['Sí', true], ['No', false]] as const).map(([etiqueta, valor]) => <label key={etiqueta} className="flex items-center gap-2"><input type="radio" disabled={!editando} checked={leer(datos, 'embarazo.si') === valor} onChange={() => onCambiar('embarazo.si', valor)} />{etiqueta}</label>)}
    </div>
    {embarazada && <div className="mt-3 grid gap-3 sm:grid-cols-2">
      <label className="text-xs text-slate-500">Tiempo de embarazo<input type="number" min="1" disabled={!editando} value={String(leer(datos, 'embarazo.tiempo') ?? '')} onChange={(e) => onCambiar('embarazo.tiempo', e.target.value)} className="mt-1 min-h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" /></label>
      <label className="text-xs text-slate-500">Unidad<select disabled={!editando} value={String(leer(datos, 'embarazo.unidad') ?? 'semanas')} onChange={(e) => onCambiar('embarazo.unidad', e.target.value)} className="mt-1 min-h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"><option value="semanas">Semanas</option><option value="meses">Meses</option></select></label>
    </div>}
  </div>
}

function HistorialConsultas({ consultas }: { consultas: ConsultaHistorial[] }) {
  return <section className="mt-6 print:hidden">
    <h2 className="mb-2 text-sm font-semibold text-slate-700">Historial de consultas</h2>
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      {consultas.length === 0 ? <p className="p-4 text-sm text-slate-500">Todavía no hay consultas registradas.</p> : <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Tratamiento / motivo</th><th className="px-4 py-3">Estado</th></tr></thead><tbody className="divide-y divide-slate-100">{consultas.map((consulta) => <tr key={consulta.id}><td className="whitespace-nowrap px-4 py-3">{consulta.inicio ? new Date(consulta.inicio).toLocaleDateString('es-PY') : 'Sin fecha'}</td><td className="px-4 py-3">{String(consulta.anamnesisOdontologica?.['motivoConsulta'] ?? consulta.notas ?? 'Sin detalle')}</td><td className="px-4 py-3 capitalize text-slate-500">{consulta.estado.replace('_', ' ')}</td></tr>)}</tbody></table></div>}
    </div>
  </section>
}
